import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BriefcaseBusiness,
  CalendarDays,
  FileText,
  Headphones,
  Home,
  LogOut,
  Menu,
  Package,
  ReceiptText,
  Ruler,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Truck,
  UserRound,
  Users,
} from "lucide-react";
import { useAuth } from "./auth/AuthContext.jsx";
import { hasPlanFeature } from "./lib/plans";

const SIDEBAR_ITEMS = [
  { to: "/", label: "Menu Principal", icon: Home },
  { to: "/ventas", label: "Venta Rapida", icon: ShoppingBag },
  { to: "/trabajos", label: "Trabajos", icon: BriefcaseBusiness },
  { to: "/presupuestos", label: "Presupuestos", icon: FileText },
  { to: "/proyectos", label: "Proyectos", icon: Ruler },
  { to: "/clientes", label: "Clientes", icon: Users },
  { to: "/proveedores", label: "Proveedores", icon: Truck },
  { to: "/maquinas", label: "Planificacion y Maquinas", icon: CalendarDays },
  { to: "/stock", label: "Stock", icon: Package },
  { to: "/compras", label: "Compras", icon: ShoppingCart, feature: "PURCHASES" },
  { to: "/remitos", label: "Remitos", icon: ReceiptText },
  { to: "/contabilidad", label: "Contabilidad", icon: BarChart3 },
  { to: "/opciones", label: "Opciones", icon: Settings },
  { to: "/soporte", label: "Servicio Tecnico", icon: Headphones },
];

function initials(user) {
  return [user?.nombre, user?.apellido]
    .filter(Boolean)
    .map((part) => String(part).charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase() || "U";
}

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appLogoFailed, setAppLogoFailed] = useState(false);

  useEffect(() => {
    setAppLogoFailed(false);
  }, []);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen min-w-0 bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <aside
        className={`relative z-30 flex shrink-0 flex-col overflow-hidden bg-slate-900 text-white shadow-xl transition-[width] duration-200 ${sidebarOpen ? "w-60" : "w-14"}`}
        onMouseEnter={() => setSidebarOpen(true)}
        onMouseLeave={() => setSidebarOpen(false)}
      >
        <div className="flex h-16 shrink-0 items-center border-b border-white/10 px-3">
          {!appLogoFailed && sidebarOpen ? (
            <div className="flex h-10 w-44 shrink-0 items-center rounded-md bg-white px-2">
              <img src="/branding/nexoprint-logo.svg" alt="NEXOPRINT" onError={() => setAppLogoFailed(true)} className="h-8 w-full object-contain object-left" />
            </div>
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-500 text-xs font-black">NP</div>
          )}
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto py-2">
          {SIDEBAR_ITEMS.filter((item) => !item.feature || hasPlanFeature(user, item.feature)).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => `mx-1.5 my-0.5 flex h-10 items-center gap-3 rounded-md px-2 transition-colors ${isActive ? "bg-indigo-500 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}
              title={label}
            >
              <Icon size={19} className="w-7 shrink-0" strokeWidth={1.9} />
              {sidebarOpen ? <span className="whitespace-nowrap text-sm font-semibold">{label}</span> : null}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" title="Abrir menu" onClick={() => setSidebarOpen((value) => !value)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white">
              <Menu size={21} />
            </button>
            <div className="flex min-w-0 items-center">
              <img src="/branding/nexoprint-logo.svg" alt="NEXOPRINT" className="h-8 w-36 object-contain object-left" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate("/mi-perfil")} title="Configurar mi usuario" className="flex h-11 min-w-0 items-center gap-2 rounded-md px-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600" />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-black text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">{initials(user)}</span>
              )}
              <span className="hidden min-w-0 sm:block">
                <span className="block max-w-44 truncate text-sm font-bold text-slate-800 dark:text-slate-100">{user?.nombreCompleto || user?.nombre || "Usuario"}</span>
                <span className="block max-w-44 truncate text-xs text-slate-500 dark:text-slate-400">{user?.impersonating ? "Acceso global" : user?.tipoUsuario?.nombre || "Usuario"}</span>
              </span>
              <UserRound size={16} className="hidden shrink-0 text-slate-400 lg:block" />
            </button>
            <button type="button" title="Cerrar sesion" onClick={signOut} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-700 dark:text-slate-300 dark:hover:bg-rose-950 dark:hover:text-rose-300">
              <LogOut size={19} />
            </button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-auto bg-slate-100 p-4 dark:bg-slate-950 sm:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
